import { Box, Typography, ButtonBase, Collapse } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPills,
  faSyringe,
  faDroplet,
  faPrescriptionBottlePill
} from "@fortawesome/pro-solid-svg-icons";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";
import { PlanMedicationCard } from "./card";
import { useState } from "react";

function getRouteIcon(route) {
  if (!route) return faPrescriptionBottlePill;
  const r = route.toLowerCase();
  if (r.includes("intra") || r.includes("iv")) return faSyringe;
  if (
    r.includes("tóp") ||
    r.includes("top") ||
    r.includes("gota") ||
    r.includes("drop")
  )
    return faDroplet;
  return faPills;
}

export function GroupCard({ group }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedMeds, setExpandedMeds] = useState({});

  const icon = getRouteIcon(group.route);

  const handleMedExpand = (id, expanded) => {
    setExpandedMeds(prev => ({ ...prev, [id]: expanded }));
  };

  return (
    <Box
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "white",
        boxShadow: isExpanded
          ? "0px 0px 24px rgba(30, 39, 79, 0.16)"
          : "0px 0px 16px rgba(30, 39, 79, 0.08)"
      }}
    >
      {/* Collapsed header */}
      {!isExpanded && (
        <ButtonBase
          onClick={() => setIsExpanded(true)}
          sx={{
            width: "100%",
            textAlign: "left",
            px: "24px",
            py: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            "&:hover": { opacity: 0.9 }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              flex: 1,
              minWidth: 0
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}
            >
              <FontAwesomeIcon
                icon={icon}
                style={{ color: semanticColors.text.primary, fontSize: 32 }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                minWidth: 0
              }}
            >
              <Typography
                sx={{
                  ...typography.heading.xl,
                  color: semanticColors.text.primary,
                  lineHeight: 1.2
                }}
              >
                {group.name}
              </Typography>
              {group.route && (
                <Box
                  sx={{
                    display: "inline-flex",
                    bgcolor: semanticColors.surface.containerLow,
                    borderRadius: "8px",
                    px: "4px",
                    py: "2px",
                    alignSelf: "flex-start"
                  }}
                >
                  <Typography
                    sx={{
                      ...typography.body.lSemibold,
                      color: semanticColors.text.secondary
                    }}
                  >
                    {group.route}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <FontAwesomeIcon
            icon={faPlus}
            style={{
              color: semanticColors.text.primary,
              fontSize: 20,
              flexShrink: 0
            }}
          />
        </ButtonBase>
      )}

      {/* Expanded gradient header */}
      {isExpanded && (
        <ButtonBase
          onClick={() => setIsExpanded(false)}
          sx={{
            width: "100%",
            textAlign: "left",
            px: "24px",
            py: "24px",
            background: semanticColors.gradient.surface,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            "&:hover": { opacity: 0.95 }
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                bgcolor: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}
            >
              <FontAwesomeIcon
                icon={icon}
                style={{ color: "white", fontSize: 28 }}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <Typography
                sx={{
                  ...typography.heading.xl,
                  color: "white",
                  lineHeight: 1.2
                }}
              >
                {group.name}
              </Typography>
              {group.route && (
                <Box
                  sx={{
                    display: "inline-flex",
                    bgcolor: "rgba(255,255,255,0.2)",
                    borderRadius: "8px",
                    px: "8px",
                    py: "2px",
                    alignSelf: "flex-start"
                  }}
                >
                  <Typography
                    sx={{ ...typography.body.lSemibold, color: "white" }}
                  >
                    {group.route}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <FontAwesomeIcon
            icon={faMinus}
            style={{ color: "white", fontSize: 20, flexShrink: 0 }}
          />
        </ButtonBase>
      )}

      {/* Expanded body */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ bgcolor: semanticColors.surface.containerLow, py: "16px" }}>
          <Box
            sx={{
              maxWidth: 832,
              mx: "auto",
              px: { xs: "16px", sm: "24px" },
              display: "flex",
              flexDirection: "column",
              gap: "12px"
            }}
          >
            {group.medications.map(med => (
              <PlanMedicationCard
                key={med.id}
                card={med}
                isExpanded={expandedMeds[med.id] || false}
                onExpand={expanded => handleMedExpand(med.id, expanded)}
                inGroup
              />
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
